import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import RIcon from "../../components/RIcon";

const sections = [
  { id: "overview", icon: "ri-home-4-line", title: "系统概述" },
  { id: "quickstart", icon: "ri-rocket-line", title: "快速开始" },
  { id: "ai-chat", icon: "ri-chat-ai-line", title: "AI 智能问答" },
  { id: "files", icon: "ri-folder-3-line", title: "文件管理" },
  { id: "doc-gen", icon: "ri-file-text-line", title: "智能文档生成" },
  { id: "content-sub", icon: "ri-rss-line", title: "内容订阅" },
  { id: "projects", icon: "ri-kanban-view-line", title: "文件资源分类" },
  { id: "admin", icon: "ri-shield-keyhole-line", title: "权限管理" },
  { id: "settings", icon: "ri-settings-3-line", title: "系统设置" },
  { id: "account", icon: "ri-user-settings-line", title: "个人账户" },
];

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

function Section({ id, children }: { id: string; children: React.ReactNode }) {
  const { ref, visible } = useReveal();
  return (
    <div
      id={id}
      ref={ref}
      className="scroll-mt-24 mb-12"
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(24px)", transition: "opacity 0.6s ease, transform 0.6s cubic-bezier(0.22,1,0.36,1)" }}
    >
      {children}
    </div>
  );
}

function SectionTitle({ icon, title, sub }: { icon: string; title: string; sub: string }) {
  return (
    <div className="flex items-start gap-4 mb-6 pb-4 border-b border-gray-100">
      <div className="w-10 h-10 rounded-xl bg-[#0EA5A0] flex items-center justify-center flex-shrink-0 mt-0.5">
        <i className={`${icon} text-white text-lg`} />
      </div>
      <div>
        <h2 className="text-xl font-bold text-[#1A2B3C]">{title}</h2>
        <p className="text-sm text-[#718096] mt-0.5">{sub}</p>
      </div>
    </div>
  );
}

function Tip({ type = "info", children }: { type?: "info" | "warn" | "tip"; children: React.ReactNode }) {
  const styles = {
    info: { bg: "bg-[#0EA5A0]/8 border-[#0EA5A0]/30", icon: "ri-information-line text-[#0EA5A0]" },
    warn: { bg: "bg-amber-50 border-amber-200", icon: "ri-alert-line text-amber-500" },
    tip: { bg: "bg-emerald-50 border-emerald-200", icon: "ri-lightbulb-line text-emerald-500" },
  }[type];
  return (
    <div className={`flex gap-3 p-4 rounded-xl border ${styles.bg} my-4`}>
      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
        <i className={`${styles.icon} text-base`} />
      </div>
      <div className="text-sm text-[#4A5568] leading-relaxed">{children}</div>
    </div>
  );
}

function Step({ num, title, desc }: { num: number; title: string; desc: string }) {
  return (
    <div className="flex gap-4 pb-6 last:pb-0">
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 rounded-full bg-[#0EA5A0] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">{num}</div>
        <div className="w-0.5 flex-1 bg-gray-100 mt-2" />
      </div>
      <div className="flex-1 pb-2">
        <p className="text-sm font-semibold text-[#1A2B3C] mb-1">{title}</p>
        <p className="text-sm text-[#718096] leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="flex gap-3 p-4 bg-[#F4F8FB] rounded-xl">
      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
        <i className={`${icon} text-[#0EA5A0] text-lg`} />
      </div>
      <div>
        <p className="text-sm font-semibold text-[#1A2B3C] mb-0.5">{title}</p>
        <p className="text-xs text-[#718096] leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

export default function ManualPage() {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      for (const s of [...sections].reverse()) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= 120) { setActiveSection(s.id); break; }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top Nav */}
      <header className="fixed top-0 left-0 w-full z-40 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2 text-[#718096] hover:text-[#0EA5A0] text-sm transition-colors">
              <RIcon name="arrow-left-line" />返回首页
            </Link>
            <span className="text-gray-300">|</span>
            <div className="flex items-center gap-2">
              <RIcon name="book-open-line" className="text-[#0EA5A0] text-base" />
              <span className="font-semibold text-[#1A2B3C] text-sm">使用手册</span>
            </div>
          </div>
          <Link to="/login" className="px-4 h-8 flex items-center justify-center bg-[#0EA5A0] text-white rounded-full text-xs font-medium hover:bg-[#0D9590] transition-colors whitespace-nowrap">
            进入系统
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 pt-20 flex gap-8">
        {/* Sidebar TOC */}
        <aside className="hidden lg:block w-52 flex-shrink-0">
          <div className="sticky top-20 pt-6">
            <p className="text-xs font-semibold text-[#718096] uppercase tracking-wider mb-3 px-2">目录</p>
            <nav className="space-y-0.5">
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-colors cursor-pointer ${
                    activeSection === s.id ? "bg-[#0EA5A0]/10 text-[#0EA5A0] font-medium" : "text-[#718096] hover:bg-gray-50 hover:text-[#1A2B3C]"
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`${s.icon} text-sm`} />
                  </div>
                  {s.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 py-8 max-w-3xl">

          {/* ── 系统概述 ── */}
          <Section id="overview">
            <SectionTitle icon="ri-home-4-line" title="系统概述" sub="协和医疗全域 AI 知识管理系统的功能定位与核心价值" />
            <p className="text-sm text-[#4A5568] leading-relaxed mb-4">
              协和医疗全域 AI 系统面向医疗、医疗器械、制药企业，把分散在各部门的研发、注册、临床、生产、质量文档统一起来，做成可检索、可问答、可追溯、可订阅的智能知识资产。
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              {[
                { icon: "ri-database-2-line", title: "知识库管理", desc: "PDF/Word/Excel/PPT/MD/音视频统一上传，自动 OCR + AI 索引建档" },
                { icon: "ri-chat-ai-line", title: "AI 智能问答", desc: "RAG 检索 + 大模型生成，回答带文件来源与相关度，可追溯校验" },
                { icon: "ri-file-text-line", title: "智能文档生成", desc: "六类合规模板，参数驱动生成 Word / PDF" },
                { icon: "ri-rss-line", title: "内容订阅", desc: "订阅 NMPA / FDA / EMA 等监管站点，按关键词自动抓取生成日报、邮件推送" },
                { icon: "ri-shield-check-line", title: "细粒度权限", desc: "部门 / 工种 / 业务阶段 / 数据权限规则多维管控，每篇文件独立可控" },
                { icon: "ri-lock-2-line", title: "数据本地化", desc: "全部署在企业内网，文档/向量/索引不出域，符合 GMP、ICH Q10、ISO 27001" },
              ].map((f) => <FeatureCard key={f.title} {...f} />)}
            </div>
            <Tip type="info">系统支持本地化私有化部署，所有数据留存企业内网；外网访问通过 HTTPS 域名（带正规 SSL 证书）暴露。</Tip>
          </Section>

          {/* ── 快速开始 ── */}
          <Section id="quickstart">
            <SectionTitle icon="ri-rocket-line" title="快速开始" sub="注册到上线问答的完整流程" />
            <div className="bg-[#F4F8FB] rounded-xl p-5 mb-4">
              <Step num={1} title="提交注册申请" desc="访问首页点击「申请注册」，填写用户名、邮箱、工号、姓名、所属部门、工种、密码。提交后账号默认禁用，等待管理员审核。" />
              <Step num={2} title="管理员审核" desc="超级管理员在「权限管理 → 用户管理 → 注册申请」中查看新申请，可点击「通过」启用账号，或「拒绝」标记不通过；审核结果会通过邮件通知申请人。" />
              <Step num={3} title="登录系统" desc="使用用户名或邮箱（任一即可）+ 密码登录。忘记密码可在登录页点击「找回密码」，通过邮箱验证码重置。" />
              <Step num={4} title="进入工作台" desc="左侧菜单按「数据概览 / 智能中心 / 内容订阅 / 资源管理 / 资源分类设置 / 权限管理」分组，普通用户看到的是其角色被授权的菜单子集。" />
              <Step num={5} title="开始使用" desc="先到「文件管理」上传文档（系统自动建索引），再到「AI 大管家」直接提问；或到「内容订阅」添加监管站点关键词订阅，让系统每天自动抓取生成日报。" />
            </div>
            <Tip type="tip">第一个注册的账号会成为超级管理员；之后的注册都需要超级管理员在「用户管理」中审核启用。</Tip>
          </Section>

          {/* ── AI 智能问答 ── */}
          <Section id="ai-chat">
            <SectionTitle icon="ri-chat-ai-line" title="AI 智能问答（AI 大管家）" sub="基于企业知识库的精准问答，每条回答都有来源引用" />
            <h3 className="text-sm font-semibold text-[#1A2B3C] mb-3">基本操作</h3>
            <div className="space-y-3 mb-5">
              {[
                { icon: "ri-send-plane-line", title: "发送问题", desc: "Enter 发送，Shift + Enter 换行。问题越具体，召回越精准（可加入业务背景，如「按 GMP 第三章 …」）。" },
                { icon: "ri-pause-circle-line", title: "暂停回答", desc: "回答流式输出过程中，发送按钮变成红色暂停按钮，点击可立即停止当前生成（已生成的内容仍保留）。" },
                { icon: "ri-filter-3-line", title: "按维度筛选", desc: "顶部筛选条支持按部门、业务阶段、工种、项目、分类多维度限制 RAG 召回范围；不选则在你权限范围内的全部文件中检索。" },
                { icon: "ri-history-line", title: "对话历史", desc: "右上角「历史」展开历史侧栏；新建对话发送后立即在历史中显示（乐观更新），不必等回答完成再刷新。" },
                { icon: "ri-file-list-line", title: "来源引用", desc: "AI 回答下方会列出来源文件（自动按文件名去重，相同文件只显示一次，相关度取最大值），点击可跳到文件详情。" },
                { icon: "ri-add-circle-line", title: "新建对话", desc: "点击「新建对话」开新一轮问答；当前对话有内容时会自动保存到历史。" },
              ].map((item) => (
                <div key={item.title} className="flex gap-3 p-4 rounded-xl border border-gray-100 hover:border-[#0EA5A0]/30 transition-colors">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-[#F4F8FB] rounded-lg">
                    <i className={`${item.icon} text-[#0EA5A0] text-base`} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#1A2B3C] mb-0.5">{item.title}</p>
                    <p className="text-xs text-[#718096] leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Tip type="tip">「来源相关度」百分比 = 该来源段与问题之间的向量相似度，越高代表段落语义越接近问题。可用于判断哪段引用更值得复核。</Tip>
            <Tip type="warn">AI 仅在你权限范围内的文档中检索；如果想问的文件在筛选结果里看不到，先确认是否已上传完成索引、且自己有数据权限。</Tip>
          </Section>

          {/* ── 文件管理 ── */}
          <Section id="files">
            <SectionTitle icon="ri-folder-3-line" title="文件管理" sub="上传企业文档，配置数据权限，AI 自动建立索引" />
            <h3 className="text-sm font-semibold text-[#1A2B3C] mb-3">上传文件</h3>
            <p className="text-sm text-[#4A5568] leading-relaxed mb-4">
              点击右上角「上传文件」，支持 PDF / Word / Excel / PPT / Markdown / 文本，以及音视频（自动 ASR 转写）。上传后填写所属部门、工种、业务阶段、所属项目、分类，并可绑定一条或多条数据权限规则；提交后系统进入「索引中」，索引完成后即可被 AI 检索。
            </p>
            <h3 className="text-sm font-semibold text-[#1A2B3C] mb-3">列表与筛选</h3>
            <div className="space-y-2 mb-4">
              {[
                { perm: "服务端分页", color: "bg-emerald-100 text-emerald-700", desc: "1800+ 文件下不再卡顿；切页直接由后端返回当前页 + 总数" },
                { perm: "来源筛选", color: "bg-sky-100 text-sky-700", desc: "默认只看「用户上传」，可切换到「订阅报告」或「全部」" },
                { perm: "关键字搜索", color: "bg-amber-100 text-amber-700", desc: "顶部搜索框按文件名服务端模糊匹配" },
                { perm: "业务阶段列", color: "bg-violet-100 text-violet-700", desc: "列表新增业务阶段列，便于按阶段排查文件" },
              ].map((item) => (
                <div key={item.perm} className="flex items-center gap-3 p-3 bg-[#F4F8FB] rounded-lg">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ${item.color}`}>{item.perm}</span>
                  <p className="text-xs text-[#718096]">{item.desc}</p>
                </div>
              ))}
            </div>
            <h3 className="text-sm font-semibold text-[#1A2B3C] mb-3">数据权限</h3>
            <p className="text-sm text-[#4A5568] leading-relaxed mb-2">
              数据权限以「规则」为单位，规则可在「权限管理 → 数据权限管理」中创建。每条规则可指定生效范围：全公司、指定部门、指定工种、指定用户。一份文件可同时绑定多条规则（OR 关系，命中任意一条即可访问）。
            </p>
            <h3 className="text-sm font-semibold text-[#1A2B3C] mb-3">批量操作</h3>
            <ul className="text-sm text-[#4A5568] space-y-1 ml-4 mb-4">
              <li className="flex items-start gap-2"><RIcon name="checkbox-circle-line" className="text-[#0EA5A0] mt-0.5" /><span><strong>批量删除</strong>：弹窗二次确认后批量删除；删除后自动刷新当前分页（即使整页都被删完也会自动跳到上一页）</span></li>
              <li className="flex items-start gap-2"><RIcon name="checkbox-circle-line" className="text-[#0EA5A0] mt-0.5" /><span><strong>批量选择数据权限</strong>：弹窗多选规则后一次性更新选中文件的权限规则集</span></li>
              <li className="flex items-start gap-2"><RIcon name="checkbox-circle-line" className="text-[#0EA5A0] mt-0.5" /><span><strong>编辑文件信息</strong>：单条编辑可修改部门、工种、业务阶段、项目、数据权限（多选）</span></li>
            </ul>
            <Tip type="info">索引完成后，文件详情页会出现 AI 智能摘要（map-reduce 生成，覆盖项目背景、文档类型、核心结论、引用到的其它文件/编号/法规、关键时间点和责任人）。</Tip>
            <Tip type="warn">如果文件长时间停留在「索引中」，系统会每 30 分钟扫描一次自动重试；也可联系管理员手动触发全量重索引。</Tip>
          </Section>

          {/* ── 智能文档生成 ── */}
          <Section id="doc-gen">
            <SectionTitle icon="ri-file-text-line" title="智能文档生成" sub="选择模板 → 填写参数 → 一键生成结构化合规报告" />
            <div className="bg-[#F4F8FB] rounded-xl p-5 mb-4">
              <Step num={1} title="选择模板" desc="预置六类医疗行业模板：质量评估报告、CAPA 报告、变更控制记录、临床试验摘要、偏差调查报告、注册申报摘要。也支持上传自定义模板。" />
              <Step num={2} title="填写公共参数" desc="报告标题、生成日期、起草人、所属部门等基础信息会自动填充到报告各章节。" />
              <Step num={3} title="填写专属参数" desc="不同模板有专属字段（如 CAPA 的问题描述、根本原因、纠正措施）；支持文本、日期、下拉选择和多行输入。" />
              <Step num={4} title="选择参考文件（可选）" desc="可挑选知识库中已有文档作为生成参考，AI 会在生成时引用相关段落，提升内容准确度。" />
              <Step num={5} title="生成与导出" desc="点击「生成报告」流式生成内容，右侧实时预览，最终一键导出 Word 或 PDF。" />
            </div>
          </Section>

          {/* ── 内容订阅 ── */}
          <Section id="content-sub">
            <SectionTitle icon="ri-rss-line" title="内容订阅" sub="按站点 + 关键词自动抓取最新公告，生成日报推送邮箱" />
            <h3 className="text-sm font-semibold text-[#1A2B3C] mb-3">创建订阅</h3>
            <p className="text-sm text-[#4A5568] leading-relaxed mb-4">
              在「订阅管理」中新建一条订阅，填写：站点（NMPA、FDA、EMA、CMDE、open.fda.gov 等监管站点；中文 / 英文均可）、关键词（多个用逗号分隔）、抓取频率（每天 / 每周 / 自定义 cron）、推送邮箱（订阅完成后会把当日报告 md 文件作为附件发送）。
            </p>
            <h3 className="text-sm font-semibold text-[#1A2B3C] mb-3">系统会自动做这些事</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              {[
                { icon: "ri-search-line", title: "多通道抓取", desc: "Crawl4AI 主链路 + Firecrawl + FlareSolverr + Stealth 浏览器多重兜底，应对反爬" },
                { icon: "ri-shield-check-line", title: "去重 & 信任源", desc: "URL 跨报告去重；NMPA / FDA 等官方源跳过严格关键词过滤" },
                { icon: "ri-translate-2-line", title: "英文自动翻中文", desc: "英文订阅源会用 LLM 整文翻译成中文追加到原文末尾，方便中文阅读和 RAG 召回" },
                { icon: "ri-mail-send-line", title: "邮件推送", desc: "完成后向订阅邮箱发送 md 附件（每篇文章一份），SMTP 走系统设置中配置的发件箱" },
                { icon: "ri-archive-line", title: "落地知识库", desc: "每篇抓到的文章独立入库为「订阅报告」类目的 md 文件，自动建索引可被 AI 问答检索" },
                { icon: "ri-history-line", title: "采集报告历史", desc: "「采集报告」页可查看每次执行的报告列表、文章数、命中关键词" },
              ].map((f) => <FeatureCard key={f.title} {...f} />)}
            </div>
            <Tip type="tip">订阅默认按一站一订阅维护，便于精细化设置不同站点的频率、关键词和邮件推送对象。</Tip>
          </Section>

          {/* ── 文件资源分类 ── */}
          <Section id="projects">
            <SectionTitle icon="ri-kanban-view-line" title="文件资源分类" sub="项目 / 部门 / 工种 / 业务阶段 — 用来给文档定位上下文" />
            <p className="text-sm text-[#4A5568] leading-relaxed mb-4">
              文件资源分类是给每份文档打上「业务上下文」标签的工具集合。在「资源分类设置」分组下：
            </p>
            <div className="space-y-3 mb-4">
              {[
                { icon: "ri-building-2-line", title: "部门管理", desc: "维护企业部门组织架构，作为文件归属和数据权限的基本单位。" },
                { icon: "ri-briefcase-line", title: "工种管理", desc: "定义研发、临床、注册、QA、QC 等工种类型，关联到该工种下的业务阶段。" },
                { icon: "ri-flow-chart", title: "业务阶段管理", desc: "在工种下细分阶段（如研发阶段下的概念、设计、验证、定型），按排序展示。" },
                { icon: "ri-folder-chart-line", title: "项目中心", desc: "统一管理研发、临床、注册等项目，跟踪进度、负责人、起止日期，文件可关联到项目。" },
              ].map((f) => <FeatureCard key={f.title} {...f} />)}
            </div>
          </Section>

          {/* ── 权限管理 ── */}
          <Section id="admin">
            <SectionTitle icon="ri-shield-keyhole-line" title="权限管理" sub="用户、角色、菜单、数据权限规则；仅管理员可见" />
            <Tip type="warn">下列菜单仅授权了 settings/users/roles/menus 等管理权限的角色才会出现在侧边栏中。</Tip>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              {[
                { icon: "ri-user-line", title: "用户管理", desc: "查看全部账号、审核新注册申请（通过/拒绝并发邮件通知）、启用/停用、重置密码、改工号 / 角色 / 部门 / 工种。" },
                { icon: "ri-shield-user-line", title: "角色管理", desc: "新建角色并配置可访问菜单和操作权限（读/写），用户挂角色即可获得对应能力。" },
                { icon: "ri-key-2-line", title: "数据权限管理", desc: "创建数据权限规则（按部门/工种/用户），规则可被批量绑定到多个文件实现按维度可见。" },
                { icon: "ri-menu-2-line", title: "菜单管理", desc: "维护侧边栏菜单结构与排序，按角色授权可见菜单。" },
              ].map((f) => <FeatureCard key={f.title} {...f} />)}
            </div>
            <Tip type="info">「注册申请」列表只显示尚未审核（approved_at 为空）的新用户；通过 / 拒绝后该用户会从申请列表移除。</Tip>
          </Section>

          {/* ── 系统设置 ── */}
          <Section id="settings">
            <SectionTitle icon="ri-settings-3-line" title="系统设置" sub="邮件 SMTP / AI 模型 等运行时配置；保存即生效，无需重启" />
            <h3 className="text-sm font-semibold text-[#1A2B3C] mb-3">邮件 SMTP</h3>
            <p className="text-sm text-[#4A5568] leading-relaxed mb-3">
              在「权限管理 → 系统设置」中可直接编辑 SMTP 服务器、端口、发件邮箱、授权码、发件人显示名以及是否使用 SSL，保存后立即应用到全局 settings 对象——本次注册审核邮件、订阅报告推送都会用最新值，无需重启后端容器。
            </p>
            <Tip type="tip">QQ 邮箱推荐配置：服务器 smtp.qq.com，端口 465，使用 SSL，密码字段填 QQ 邮箱「账户安全」中生成的「授权码」（不是登录密码）。</Tip>
            <h3 className="text-sm font-semibold text-[#1A2B3C] mb-3 mt-5">运行配置（只读）</h3>
            <p className="text-sm text-[#4A5568] leading-relaxed mb-3">
              对话模型 / Embedding / PageIndex 模型 / 视觉模型 / vLLM 网关地址等，会显示当前后端实际加载的运行值（来自环境变量 + DB 覆盖）；如需调整，请联系运维改 .env 或通过同页面写入运行时配置。
            </p>
          </Section>

          {/* ── 个人账户 ── */}
          <Section id="account">
            <SectionTitle icon="ri-user-settings-line" title="个人账户" sub="个人信息、修改密码、找回密码" />
            <h3 className="text-sm font-semibold text-[#1A2B3C] mb-3">个人设置</h3>
            <p className="text-sm text-[#4A5568] leading-relaxed mb-4">
              点击右上角头像 → 「个人设置」可修改姓名、所属部门、工种、邮箱、手机号、个人简介；保存后即时生效。
            </p>
            <h3 className="text-sm font-semibold text-[#1A2B3C] mb-3">修改密码</h3>
            <p className="text-sm text-[#4A5568] leading-relaxed mb-3">
              点击右上角头像 → 「修改密码」，输入当前密码 + 新密码（至少 8 位）即可。新密码强度会实时提示。
            </p>
            <h3 className="text-sm font-semibold text-[#1A2B3C] mb-3">忘记密码</h3>
            <p className="text-sm text-[#4A5568] leading-relaxed mb-3">
              在登录页点击「找回密码」，输入用户名 + 注册时填写的邮箱，系统会向该邮箱发送 6 位验证码（10 分钟有效），验证通过后即可设置新密码。仅启用状态的账号可走自助找回。
            </p>
            <Tip type="warn">如果邮箱已停用或验证码收不到，请联系管理员在「用户管理」中执行密码重置。</Tip>

            <div className="mt-6 p-5 bg-gradient-to-r from-[#0EA5A0]/10 to-[#06B6D4]/10 rounded-xl border border-[#0EA5A0]/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <RIcon name="customer-service-2-line" className="text-[#0EA5A0] text-base" />
                </div>
                <span className="text-sm font-semibold text-[#1A2B3C]">需要帮助？</span>
              </div>
              <p className="text-xs text-[#718096] leading-relaxed">
                使用过程中遇到问题，可联系企业内部系统管理员，或发送邮件至技术支持邮箱。管理员在「用户管理」中可处理账号相关任何事项。
              </p>
            </div>
          </Section>

        </main>
      </div>
    </div>
  );
}
